import { merge } from "lodash-es";
import pluralize from "pluralize";
import { clone } from "ramda";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { encodeFilter, type IRsqlComparisonExpr, SEARCH_OPERATORS } from "@/app-utils/rsql.ts";
import { type ContentBrowser, type InputTableQuerySpec, type ListingMapEntry, type View } from "@/graphql.ts";
import {
  CONTENT_BROWSER_CHANGE_SELECTION,
  FILTER_BY_TABLE_COLUMN,
  LOAD_COUNTS_FAILURE,
  LOAD_COUNTS_REQUEST,
  LOAD_COUNTS_SUCCESS,
  LOAD_INFO_FAILURE,
  LOAD_INFO_REQUEST,
  LOAD_INFO_SUCCESS,
  LOAD_LISTING_FAILURE,
  LOAD_LISTING_REQUEST,
  LOAD_LISTING_SUCCESS,
  LOAD_VIEW_SUCCESS,
  RESET_CONTENT_BROWSER_STATE,
  RESET_FILTER,
} from "@/redux/action-types.ts";
import {
  GROUP_BY_ATTRIBUTE_FEATURES,
  GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
  GROUP_BY_ATTRIBUTE_SPECIES,
  GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
  GROUP_BY_TAXONOMIC_GROUP,
  ORGANISM_TABLE_COLUMNS,
  VIEW,
} from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { createReducer } from "../redux-utils.js";
import { createTableSpecForRootEntityType, filterByTableColumn, type IFilterPayload } from "./table-data.js";

// since we want the user to be able to filter by the sequencingType (although its an ENUM)
// we will also make use of the labels to create dynamically filters in the content browser from the information text figures
const SEQUENCING_TYPE_LABELS = {
  RE_SEQUENCED: "Re-sequenced",
  SEQUENCED_ASSEMBLY: "Sequenced Assembly",
  VIRTUAL: "Virtual Genome",
  VECTOR: "Vector Genome",
  PROTEIN_SYNTENY_SET: "Protein Synteny Set",
  GENE_SYNTENY_SET: "Gene Synteny Set",
  PANGENOME_SET: "Pangenome Graph",
};

/**
 * State definition
 */
export interface IContentBrowserState {
  content: ContentBrowser;
  isLoadingCounts: boolean;
  isLoadingListing: boolean;
  isLoadingInfo: boolean;
  selectedEntry: ListingMapEntry;
  featureType: string;
  groupByAttribute: string;
}

const initialState: IContentBrowserState = {
  content: null,
  isLoadingCounts: false,
  isLoadingListing: false,
  isLoadingInfo: false,
  selectedEntry: null,
  featureType: null,
  groupByAttribute: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_COUNTS_REQUEST]: (state: IContentBrowserState): IContentBrowserState => {
    return { ...state, isLoadingCounts: true };
  },

  [LOAD_VIEW_SUCCESS]: (state: IContentBrowserState, view: View) => {
    if (view.viewType === VIEW.CONTENT_BROWSER_VIEW.type) {
      // Set the minimum data for ContentBrowser to load additional info. listing and counts
      // The content browser table is initialized in the table-data Redux module
      const contentBrowserViewState = view.state?.contentBrowserView;
      return {
        ...state,
        featureType: contentBrowserViewState?.tableView?.tableQuerySpec?.resultType,
        selectedEntry: clone(contentBrowserViewState?.selectedEntry),
        groupByAttribute: contentBrowserViewState?.groupByAttribute,
      };
    }
    return state;
  },

  [LOAD_LISTING_REQUEST]: (state: IContentBrowserState, { featureType, groupByAttribute }): IContentBrowserState => {
    return { ...state, isLoadingListing: true, featureType, groupByAttribute };
  },
  [LOAD_INFO_REQUEST]: (state: IContentBrowserState): IContentBrowserState => {
    return { ...state, isLoadingInfo: true };
  },

  [LOAD_COUNTS_SUCCESS]: (state: IContentBrowserState, content: ContentBrowser): IContentBrowserState => {
    return { ...state, isLoadingCounts: false, content: merge(clone(state.content), content) };
  },
  [LOAD_LISTING_SUCCESS]: (state: IContentBrowserState, content: ContentBrowser): IContentBrowserState => {
    return {
      ...state,
      isLoadingListing: false,
      content: merge(clone(state.content), content),
    };
  },
  [LOAD_INFO_SUCCESS]: (state: IContentBrowserState, content: ContentBrowser): IContentBrowserState => {
    return {
      ...state,
      isLoadingInfo: false,
      content: merge(clone(state.content), content),
    };
  },

  [LOAD_COUNTS_FAILURE]: (state: IContentBrowserState): IContentBrowserState => {
    return { ...state, isLoadingCounts: false };
  },
  [LOAD_LISTING_FAILURE]: (state: IContentBrowserState): IContentBrowserState => {
    return { ...state, isLoadingListing: false };
  },
  [LOAD_INFO_FAILURE]: (state: IContentBrowserState): IContentBrowserState => {
    return { ...state, isLoadingInfo: false };
  },
  [CONTENT_BROWSER_CHANGE_SELECTION]: (state: IContentBrowserState, entry: ListingMapEntry): IContentBrowserState => {
    const newValue: ListingMapEntry =
      !entry || (state.selectedEntry && state.selectedEntry.key === entry.key) ? null : entry;
    return { ...state, selectedEntry: newValue };
  },
  [RESET_FILTER]: (state: IContentBrowserState) => {
    return { ...state, selectedEntry: null };
  },
  [RESET_CONTENT_BROWSER_STATE]: (): IContentBrowserState => {
    return { ...initialState };
  },
  [FILTER_BY_TABLE_COLUMN]: (state: IContentBrowserState, payload) => {
    const { columnField, filterExpression } = payload;
    // if the filter has been manually changed by the user we unset the selected entry
    if (
      state.selectedEntry &&
      state.selectedEntry.columnField === columnField &&
      state.selectedEntry.tableFilter !== filterExpression
    ) {
      return { ...state, selectedEntry: null };
    }
    return state;
  },
});

/**
 * Action creators
 */
export function loadContentBrowserCounts() {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<ContentBrowser> => {
    const contentBrowserState: IContentBrowserState = getState().contentBrowser;

    if (!contentBrowserState.isLoadingCounts) {
      dispatch({ type: LOAD_COUNTS_REQUEST });

      return webApiClient
        .fetchContentBrowserCounts()
        .then((response: ContentBrowser) => {
          dispatch({
            type: LOAD_COUNTS_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_COUNTS_FAILURE,
            alertTitle: "Fetching of Content Browser Counts",
          }),
        );
    }
    return Promise.resolve(null);
  };
}

export function loadContentBrowserListing(featureType: string, groupByAttribute: string) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<ContentBrowser> => {
    const contentBrowserState: IContentBrowserState = getState().contentBrowser;

    if (!contentBrowserState.isLoadingListing) {
      dispatch({
        type: LOAD_LISTING_REQUEST,
        payload: { featureType, groupByAttribute },
      });

      return webApiClient
        .fetchContentBrowserListing(featureType, groupByAttribute)
        .then((response: ContentBrowser) => {
          dispatch({
            type: LOAD_LISTING_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_LISTING_FAILURE,
            alertTitle: `Fetching of Content Browser Listing for Feature Type '${featureType}' Grouped by '${groupByAttribute}'`,
          }),
        );
    }
    return Promise.resolve(null);
  };
}

export function resetContentBrowserState() {
  return (dispatch) => {
    dispatch({ type: RESET_CONTENT_BROWSER_STATE });
  };
}

export function loadContentBrowserInfo(featureType: string, groupByAttribute: string, filterKey: string = null) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<ContentBrowser> => {
    const contentBrowserState: IContentBrowserState = getState().contentBrowser;

    if (!contentBrowserState.isLoadingInfo) {
      dispatch({ type: LOAD_INFO_REQUEST });

      return webApiClient
        .fetchContentBrowserInfo(featureType, groupByAttribute, filterKey)
        .then((response: ContentBrowser) => {
          dispatch({
            type: LOAD_INFO_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_INFO_FAILURE,
            alertTitle: `Fetching of Content Browser Info for Feature Type '${featureType}' Grouped by '${groupByAttribute}'`,
          }),
        );
    }
    return Promise.resolve(null);
  };
}

export function changeGroupByAttributeFilter(entry: ListingMapEntry) {
  return (dispatch, getState) => {
    const contentBrowserState: IContentBrowserState = getState().contentBrowser;
    const featureType: string = contentBrowserState.featureType;
    // toggle the filter if already selected
    const filter: ListingMapEntry =
      contentBrowserState.selectedEntry && entry && contentBrowserState.selectedEntry.key === entry.key ? null : entry;
    const tableSpec: InputTableQuerySpec = createContentBrowserTableSpec(featureType);

    const filterPayload: IFilterPayload = filter
      ? createFilterPayloadForSelectedBar(entry, tableSpec)
      : ({ tableSpec } as IFilterPayload);
    dispatch(filterByTableColumn(filterPayload, true));
    dispatch({
      type: CONTENT_BROWSER_CHANGE_SELECTION,
      payload: filter,
    });
  };
}

export function changeStatisticsFilter(filterColumnField: string, filter: IRsqlComparisonExpr) {
  return (dispatch, getState) => {
    const contentBrowserState: IContentBrowserState = getState().contentBrowser;
    const featureType: string = contentBrowserState.featureType;
    const selectedGroupByAttributeEntry: ListingMapEntry = contentBrowserState.selectedEntry;
    const tableSpec: InputTableQuerySpec = createContentBrowserTableSpec(featureType);

    // create the statistics filter
    const filters: IFilterPayload[] = [
      {
        columnField: filterColumnField,
        tableSpec,
        filterExpression: encodeFilter(filter),
      },
    ];
    // add the filter for the selected bar (if any) since we will reset all other filters
    if (selectedGroupByAttributeEntry) {
      filters.push(createFilterPayloadForSelectedBar(selectedGroupByAttributeEntry, tableSpec));
    }

    // if a filter is set we apply it to the corresponding columns, and we also ignore the previous filters !
    dispatch(filterByTableColumn(filters, true));
  };
}

export function createContentBrowserTableSpec(pluralFeatureType: string) {
  return createTableSpecForRootEntityType(pluralize.singular(pluralFeatureType));
}

export interface IGenomeTableFiltersType {
  SEQUENCED_GENOMES: IRsqlComparisonExpr;
  PUBLIC_AND_ASSEMBLED_GENOMES: IRsqlComparisonExpr;
  GENOMES_ALIGNED_FROM_ALIGNED_NGS_READS: IRsqlComparisonExpr;
  REFERENCE_GENOME: IRsqlComparisonExpr;
}

export const CONTENT_BROWSER_GENOME_TABLE_FILTERS: IGenomeTableFiltersType = {
  SEQUENCED_GENOMES: {
    key: ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
    operator: SEARCH_OPERATORS.IN,
    value: [
      SEQUENCING_TYPE_LABELS.SEQUENCED_ASSEMBLY,
      SEQUENCING_TYPE_LABELS.RE_SEQUENCED,
      SEQUENCING_TYPE_LABELS.VIRTUAL,
      SEQUENCING_TYPE_LABELS.VECTOR,
      SEQUENCING_TYPE_LABELS.PROTEIN_SYNTENY_SET,
      SEQUENCING_TYPE_LABELS.GENE_SYNTENY_SET,
      SEQUENCING_TYPE_LABELS.PANGENOME_SET,
    ],
  },
  PUBLIC_AND_ASSEMBLED_GENOMES: {
    key: ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
    operator: SEARCH_OPERATORS.IN,
    value: [SEQUENCING_TYPE_LABELS.SEQUENCED_ASSEMBLY],
  },
  GENOMES_ALIGNED_FROM_ALIGNED_NGS_READS: {
    key: ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
    operator: SEARCH_OPERATORS.IN,
    value: [SEQUENCING_TYPE_LABELS.RE_SEQUENCED],
  },
  REFERENCE_GENOME: {
    key: ORGANISM_TABLE_COLUMNS.REF_GENOME,
    operator: SEARCH_OPERATORS.EQUAL,
    value: "true",
  },
};

// which organism table columns to be shown when navigating through content browser "group by" attributes
export const VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE = {
  [GROUP_BY_ATTRIBUTE_SPECIES]: [ORGANISM_TABLE_COLUMNS.NAME, ORGANISM_TABLE_COLUMNS.SPECIES],
  [GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE]: [
    ORGANISM_TABLE_COLUMNS.NAME,
    ORGANISM_TABLE_COLUMNS.SPECIES,
    ORGANISM_TABLE_COLUMNS.SEQUENCING_TYPE,
  ],
  [GROUP_BY_ATTRIBUTE_FEATURES]: [
    ORGANISM_TABLE_COLUMNS.NAME,
    ORGANISM_TABLE_COLUMNS.NO_CONTIGS,
    ORGANISM_TABLE_COLUMNS.NO_GENES,
    ORGANISM_TABLE_COLUMNS.NO_TRANSCRIPTS,
    ORGANISM_TABLE_COLUMNS.NO_PROTEINS,
    ORGANISM_TABLE_COLUMNS.EC_COVERAGE,
  ],
  [GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP]: [
    ORGANISM_TABLE_COLUMNS.NAME,
    ORGANISM_TABLE_COLUMNS.SPECIES,
    ORGANISM_TABLE_COLUMNS.VARIANT_ANALYSIS_GROUP,
    ORGANISM_TABLE_COLUMNS.REF_GENOME,
  ],
  [GROUP_BY_TAXONOMIC_GROUP]: [
    ORGANISM_TABLE_COLUMNS.NAME,
    ORGANISM_TABLE_COLUMNS.SPECIES,
    ORGANISM_TABLE_COLUMNS.TAXONOMY,
  ],
};

function createFilterPayloadForSelectedBar(
  selectedEntry: ListingMapEntry,
  tableSpec: InputTableQuerySpec,
): IFilterPayload {
  return {
    columnField: selectedEntry.columnField,
    tableSpec,
    filterExpression: selectedEntry.tableFilter,
  };
}
